'use client';

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ExclamationSign from '@svenskaspelab/ui/react/icons/ExclamationSign';
import { Input } from '@svenskaspelab/ui/react/input';
import { ToggleGroup, ToggleGroupItem } from '@svenskaspelab/ui/react/toggle-group';
import { useEffect, useRef, useState } from 'react';
const DEFAULT_ACCESSIBILITY_ERROR_MESSAGE = 'Felaktigt insatsbelopp angivet';
/**
 * Component that renders a button/radio group of betamount options, and also an input field
 * The buttons calls the callback function to change the state immediately.
 * The input field is mirroring the state value but is debouncing the onChange callback when the value does not match the zod schema.
 * This is to prevent displaying errors when the user is actively typing.
 *
 * @param root0 input object
 * @param root0.betAmounts list of the preset betamount values.
 * @param root0.currentBetAmount the currently selected betamount value (current state)
 * @param root0.onBetAmountChange callback function to change the state
 * @param root0.errorMessage error message, validation is done on the parent, thus the error is passed down.
 * @returns Component
 */
export default function CouponAdditionsBetAmount({
  betAmounts,
  currentBetAmount,
  onBetAmountChange,
  errorMessage
}) {
  const [inputValue, setInputValue] = useState(currentBetAmount.toString());
  const betAmountInputRef = useRef(null);
  const [accessibilityErrorMessage, setAccessibilityErrorMessage] = useState('');
  const oldBetAmount = useRef(currentBetAmount);
  const hasUserInteractedRef = useRef(false);
  const [shouldShowInput, setShouldShowInput] = useState(!betAmounts.includes(currentBetAmount));
  const [lastNonEmptyInputValue, setLastNonEmptyInputValue] = useState(currentBetAmount.toString());
  const isInputAmountPreset = betAmounts.includes(Number.parseInt(inputValue, 10));
  const isLastNonEmptyInputAmountPreset = betAmounts.includes(Number.parseInt(lastNonEmptyInputValue, 10));
  const selectedToggleValue = shouldShowInput ? inputValue.length === 0 ? isLastNonEmptyInputAmountPreset ? lastNonEmptyInputValue : 'annan' : isInputAmountPreset ? inputValue : 'annan' : inputValue;
  // useEffect to handle focus and blur events on the input field to manage accessibility error messages
  useEffect(() => {
    const handleBlur = () => {
      if (errorMessage.length > 0) {
        setAccessibilityErrorMessage(DEFAULT_ACCESSIBILITY_ERROR_MESSAGE);
      }
    };
    const handleFocus = () => {
      setAccessibilityErrorMessage('');
    };
    const inputElement = betAmountInputRef.current;
    if (inputElement) {
      inputElement.addEventListener('blur', handleBlur);
      inputElement.addEventListener('focus', handleFocus);
    }
    return () => {
      if (inputElement) {
        inputElement.removeEventListener('blur', handleBlur);
        inputElement.removeEventListener('focus', handleFocus);
      }
    };
  }, [errorMessage]);
  useEffect(() => {
    if (oldBetAmount.current !== currentBetAmount) {
      oldBetAmount.current = currentBetAmount;
      setInputValue(currentBetAmount.toString());
      setLastNonEmptyInputValue(currentBetAmount.toString());
      if (!hasUserInteractedRef.current) {
        // Keep first-load behavior correct even when value arrives after mount.
        // Once the user has interacted, visibility is controlled by explicit toggle actions.
        setShouldShowInput(!betAmounts.includes(currentBetAmount));
      }
    }
  }, [betAmounts, currentBetAmount]);
  const handleInputChange = e => {
    hasUserInteractedRef.current = true;
    const value = e.target.value.replaceAll(/\D/g, '');
    setInputValue(value);
    const parsedValue = Number.parseInt(value, 10);
    if (value.length > 0) {
      setLastNonEmptyInputValue(value);
    }
    onBetAmountChange(parsedValue);
  };
  const handToggleGroupChange = value_0 => {
    hasUserInteractedRef.current = true;
    if (value_0 === 'annan') {
      setShouldShowInput(true);
      return;
    }
    setShouldShowInput(false);
    setLastNonEmptyInputValue(value_0);
    setInputValue(value_0);
    onBetAmountChange(Number.parseInt(value_0, 10));
  };
  const labels = betAmounts.map((amount, index) => {
    const label = {
      labelText: index === 0 ? '' : `${amount} x vinst`,
      key: `${amount} x vinst`
    };
    return label;
  });
  return _jsxs("div", {
    children: [_jsxs("div", {
      children: [_jsx("div", {
        className: "text-surface-fg-muted pb-[12px] text-center text-sm lg:text-left",
        children: `${labels.map(label_0 => label_0.labelText).filter(Boolean).join(', ')} osv.`
      }), _jsxs("div", {
        className: "flex flex-col gap-1 lg:gap-2",
        children: [_jsxs(ToggleGroup, {
          size: "sm",
          type: "single",
          onValueChange: handToggleGroupChange,
          value: selectedToggleValue,
          className: "border-stroke-medium flex w-full gap-0",
          context: "base",
          emphasis: "low",
          children: [betAmounts.map(amount_0 => _jsx(ToggleGroupItem, {
            value: amount_0.toString(),
            role: "radio",
            "aria-checked": selectedToggleValue === amount_0.toString(),
            "data-state": selectedToggleValue === amount_0.toString() ? 'on' : 'off',
            "aria-label": `${amount_0} x vinst`,
            className: "border-stroke-medium h-10 w-full rounded-none border border-l-0 whitespace-nowrap first:rounded-l-md first:border-l data-[state=on]:border-l",
            children: `${amount_0} kr`
          }, amount_0)), _jsx(ToggleGroupItem, {
            value: "annan",
            role: "radio",
            "aria-checked": selectedToggleValue === 'annan',
            "data-state": selectedToggleValue === 'annan' ? 'on' : 'off',
            "aria-label": "Annan",
            className: "border-stroke-medium h-10 w-full rounded-l-none border border-l-0 whitespace-nowrap",
            children: "Annan"
          })]
        }), shouldShowInput && _jsx(Input, {
          "aria-describedby": "additionBetAmountInputErrortext",
          "aria-invalid": accessibilityErrorMessage.length > 0,
          "aria-label": "betamount-input",
          placeholder: "Annan",
          className: "bet-amount-input",
          "data-testid": "betAmountInput",
          value: inputValue,
          onChange: handleInputChange,
          suffix: "kr",
          ref: betAmountInputRef
        })]
      })]
    }), errorMessage.length > 0 && _jsxs("span", {
      className: "flex items-center justify-end pt-1",
      children: [_jsx(ExclamationSign, {
        className: "text-important mr-1 inline",
        fontSize: "24"
      }), errorMessage]
    }), _jsx("div", {
      "aria-atomic": "true",
      "aria-live": "polite",
      className: "sr-only",
      id: "additionBetAmountInputErrortext",
      children: accessibilityErrorMessage.length > 0 && _jsx("span", {
        children: accessibilityErrorMessage
      })
    })]
  });
}